/**
 * SessionDrawer
 * ─────────────
 * Extracted from EventScheduleTab for reuse in ScheduleElement canvas widget.
 * Handles editing a single schedule item including time, speakers, location, photo.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Drawer, Divider, IconButton, Avatar, Grid, Tooltip,
} from '@mui/material';
import {
  CloseOutlined, SaveOutlined, DeleteOutlined, ArrowForwardOutlined,
  PersonAddOutlined, EditOutlined, AddAPhotoOutlined, PhotoCameraOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { v4 as uuid } from 'uuid';
import { PRIMARY, INPUT_SX, FormField } from '../shared';
import { ContentBrowserModal } from '../lobbyBuilderTab/ContentBrowserModal';

const BLANK = {
  title: '', description: '', dayLabel: '', dayNumber: 1,
  startTime: '', endTime: '', timeSlots: [], speakers: [], location: '', type: 'session', order: 0, posterUrl: '',
};

const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const val  = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const hr   = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? 'AM' : 'PM';
      opts.push({ val, label: `${hr}:${String(m).padStart(2, '0')} ${ampm}` });
    }
  }
  return opts;
})();

function fmtTime(t) {
  if (!t || !t.includes(':')) return '';
  const [h, m] = t.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return '';
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hr}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

function TimePickerPopup({ value, onChange, onClose }) {
  const listRef = useRef();
  useEffect(() => {
    if (!listRef.current) return;
    const sel = listRef.current.querySelector('[data-selected="true"]');
    if (sel) sel.scrollIntoView({ block: 'center' });
  }, []);
  return (
    <Box sx={{ position: 'absolute', top: '100%', left: 0, zIndex: 1400, width: 160, mt: 0.5,
      background: '#fff', border: '1px solid #e4e9f0', borderRadius: 2,
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)', overflow: 'hidden' }}>
      <Box ref={listRef} sx={{ maxHeight: 220, overflowY: 'auto' }}>
        {TIME_OPTIONS.map(opt => {
          const selected = opt.val === value;
          return (
            <Box key={opt.val} data-selected={selected} onClick={() => { onChange(opt.val); onClose(); }}
              sx={{ px: 2, py: 0.9, cursor: 'pointer', fontSize: '0.85rem', fontWeight: selected ? 700 : 400,
                color: selected ? PRIMARY : '#374151', background: selected ? alpha(PRIMARY, 0.08) : 'transparent',
                '&:hover': { background: selected ? alpha(PRIMARY, 0.12) : '#f5f7fa' } }}>
              {opt.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function TimePicker({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <Box ref={ref} sx={{ position: 'relative', flex: 1 }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 0.5 }}>{label}</Typography>
      <Box onClick={() => setOpen(o => !o)} sx={{ border: '1px solid #e8edf2', borderRadius: 2, background: '#f8f9fb',
        px: 1.5, py: 0.85, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        '&:hover': { borderColor: PRIMARY } }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: value ? 600 : 400, color: value ? '#1a1a2e' : '#9ea8b3' }}>
          {fmtTime(value) || 'Select time'}
        </Typography>
        <Box component="span" sx={{ fontSize: '0.6rem', color: '#9ea8b3' }}>▾</Box>
      </Box>
      {open && <TimePickerPopup value={value} onChange={onChange} onClose={() => setOpen(false)} />}
    </Box>
  );
}

function SessionPhotoField({ value, onChange }) {
  const [browserOpen, setBrowserOpen] = useState(false);
  return (
    <Box sx={{ mb: 2 }}>
      <Box onClick={() => setBrowserOpen(true)} sx={{ width: '100%', height: 140, borderRadius: 2,
        background: value ? 'transparent' : alpha(PRIMARY, 0.04),
        border: value ? 'none' : `2px dashed ${alpha(PRIMARY, 0.2)}`,
        overflow: 'hidden', cursor: 'pointer', position: 'relative', '&:hover .photo-overlay': { opacity: 1 } }}>
        {value
          ? <Box component="img" src={value} alt="session" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
              <AddAPhotoOutlined sx={{ fontSize: 24, color: alpha(PRIMARY, 0.35) }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: alpha(PRIMARY, 0.5) }}>Add session photo</Typography>
            </Box>
        }
        <Box className="photo-overlay" sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.32)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.18s' }}>
          <AddAPhotoOutlined sx={{ color: '#fff', fontSize: 26 }} />
        </Box>
        {value && (
          <Box onClick={e => { e.stopPropagation(); onChange(''); }}
            sx={{ position: 'absolute', top: 8, right: 8, width: 24, height: 24, borderRadius: '50%',
              background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <CloseOutlined sx={{ fontSize: 14, color: '#fff' }} />
          </Box>
        )}
      </Box>
      <ContentBrowserModal open={browserOpen} onClose={() => setBrowserOpen(false)}
        onSelect={url => { onChange(url); setBrowserOpen(false); }} mediaType="image" currentUrl={value || ''} />
    </Box>
  );
}

function isValidUrl(str) {
  if (!str) return true;
  try { new URL(str); return true; } catch { return false; }
}

const PORTRAIT_RATIO = 3.5 / 4;

function SpeakerCard({ speaker, onRemove, onEdit, hideEdit = false }) {
  const name    = typeof speaker === 'string' ? speaker : speaker.name || '';
  const avatar  = typeof speaker === 'object' ? speaker.avatar : null;
  const title   = typeof speaker === 'object' ? speaker.title : null;
  const company = typeof speaker === 'object' ? speaker.company : null;
  const hue     = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  const initials= name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const THUMB   = 36;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75,
      borderRadius: 1.5, border: `1px solid ${alpha(PRIMARY, 0.13)}`, background: '#fafbfc',
      '&:hover': { borderColor: alpha(PRIMARY, 0.3), background: alpha(PRIMARY, 0.03) } }}>
      <Box sx={{ width: THUMB, height: THUMB / PORTRAIT_RATIO, borderRadius: 1, overflow: 'hidden',
        flexShrink: 0, background: `hsl(${hue},35%,92%)`, position: 'relative' }}>
        {avatar
          ? <Box component="img" src={avatar} alt={name} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          : <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: `hsl(${hue},40%,45%)`, lineHeight: 1 }}>{initials}</Typography>
            </Box>
        }
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>{name}</Typography>
        {(title || company) && (
          <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
            {[title, company].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
        {!hideEdit && (
          <Tooltip title="Edit speaker">
            <IconButton size="small" onClick={onEdit} sx={{ width: 22, height: 22, color: '#b0bac8', '&:hover': { color: PRIMARY, background: alpha(PRIMARY, 0.08) } }}>
              <EditOutlined sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Remove">
          <IconButton size="small" onClick={onRemove} sx={{ width: 22, height: 22, color: '#b0bac8', '&:hover': { color: '#d32f2f', background: alpha('#d32f2f', 0.08) } }}>
            <CloseOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

function SpeakerPickerPopup({ eventSpeakers, selected, onSelect, onClose, onAddCustom }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef();
  useEffect(() => { inputRef.current?.focus(); }, []);
  const selectedIds = new Set(selected.map(s => s.id).filter(Boolean));
  const trimmed = query.trim();
  const filtered = eventSpeakers.filter(sp =>
    (sp.name || '').toLowerCase().includes(trimmed.toLowerCase()) && !selectedIds.has(sp.id)
  );
  const showAddNew = trimmed.length > 0 && !eventSpeakers.some(sp => (sp.name || '').toLowerCase() === trimmed.toLowerCase());
  const handleAddNew = () => { onAddCustom({ id: uuid(), name: trimmed }); setQuery(''); onClose(); };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 1.25, borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
        <TextField inputRef={inputRef} value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && trimmed) handleAddNew(); if (e.key === 'Escape') onClose(); }}
          fullWidth size="small" placeholder="Search or type a name to add…"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          InputProps={{ endAdornment: trimmed ? (
            <Button size="small" variant="contained" onMouseDown={e => { e.preventDefault(); handleAddNew(); }}
              sx={{ borderRadius: 1, height: 24, fontSize: '0.72rem', px: 1, background: PRIMARY, textTransform: 'none', flexShrink: 0 }}>
              Add
            </Button>
          ) : null }}
        />
        {showAddNew && (
          <Box onMouseDown={e => { e.preventDefault(); handleAddNew(); }}
            sx={{ mt: 0.75, px: 1.25, py: 0.75, borderRadius: 1.5, border: `1px dashed ${alpha(PRIMARY, 0.3)}`,
              background: alpha(PRIMARY, 0.03), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
              '&:hover': { background: alpha(PRIMARY, 0.08) } }}>
            <PersonAddOutlined sx={{ fontSize: 14, color: PRIMARY }} />
            <Typography sx={{ fontSize: '0.8rem', color: PRIMARY, fontWeight: 600 }}>Add "{trimmed}" as new speaker</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        {filtered.length === 0 && !showAddNew && (
          <Typography sx={{ px: 1, py: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
            {eventSpeakers.length === 0 ? 'No speakers added to this event yet.' : trimmed ? 'No matching speakers.' : 'All speakers already added.'}
          </Typography>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          {filtered.map(sp => {
            const hue = [...(sp.name || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
            const initials = (sp.name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
            return (
              <Box key={sp.id} onMouseDown={e => { e.preventDefault(); onSelect(sp); onClose(); }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5, cursor: 'pointer',
                  border: `1px solid ${alpha(PRIMARY, 0.1)}`, background: '#fafbfc',
                  '&:hover': { background: alpha(PRIMARY, 0.06), borderColor: alpha(PRIMARY, 0.25) } }}>
                {sp.avatar
                  ? <Box component="img" src={sp.avatar} alt={sp.name} sx={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  : <Avatar sx={{ width: 30, height: 30, fontSize: '0.65rem', fontWeight: 700, bgcolor: `hsl(${hue},55%,85%)`, color: `hsl(${hue},40%,30%)`, flexShrink: 0 }}>{initials}</Avatar>
                }
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sp.name}</Typography>
                  {sp.title && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sp.title}</Typography>}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function SpeakerEditorDrawer({ open, speaker, onClose, onSave }) {
  const [form, setForm]               = useState({ id: uuid(), name: '', title: '', company: '', bio: '', avatar: '', track: '', order: 0, socialLinks: { linkedin: '', twitter: '', website: '' } });
  const [photoBrowser, setPhotoBrowser] = useState(false);
  const [urlErrors, setUrlErrors]       = useState({});
  useEffect(() => {
    if (!open) return;
    setForm(speaker
      ? { id: speaker.id || uuid(), name: '', title: '', company: '', bio: '', avatar: '', track: '', order: 0, socialLinks: { linkedin: '', twitter: '', website: '' }, ...speaker, socialLinks: { linkedin: '', twitter: '', website: '', ...(speaker.socialLinks || {}) } }
      : { id: uuid(), name: '', title: '', company: '', bio: '', avatar: '', track: '', order: 0, socialLinks: { linkedin: '', twitter: '', website: '' } }
    );
    setUrlErrors({});
  }, [speaker, open]);
  const set       = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setSocial = (k, v) => { setForm(p => ({ ...p, socialLinks: { ...p.socialLinks, [k]: v } })); setUrlErrors(e => ({ ...e, [k]: v && !isValidUrl(v) })); };
  const hasUrlError = Object.values(urlErrors).some(Boolean);
  const canSave     = form.name.trim() && !hasUrlError;
  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 460 }, display: 'flex', flexDirection: 'column', zIndex: 1600 } }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{speaker ? 'Edit Speaker' : 'Add Speaker'}</Typography>
          <IconButton size="small" onClick={onClose}><CloseOutlined sx={{ fontSize: 18 }} /></IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
          <FormField label="Photo">
            <Box onClick={() => setPhotoBrowser(true)} sx={{ width: '100%', aspectRatio: '3.5/4', borderRadius: 2, overflow: 'hidden',
              border: '2px dashed #d1d9e6', cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
              '&:hover': { borderColor: PRIMARY }, '&:hover .photo-overlay': { opacity: 1 } }}>
              {form.avatar
                ? <Box component="img" src={form.avatar} alt="Speaker photo" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: '#9ea8b3' }}>
                    <PhotoCameraOutlined sx={{ fontSize: 32 }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Click to upload photo</Typography>
                  </Box>
              }
              <Box className="photo-overlay" sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, background: 'rgba(0,0,0,0.45)', opacity: 0, transition: 'opacity 0.15s' }}>
                <PhotoCameraOutlined sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
            </Box>
          </FormField>
          <FormField label="Full Name" required>
            <TextField value={form.name} onChange={e => set('name', e.target.value)} fullWidth size="small" placeholder="Jane Doe" sx={INPUT_SX} />
          </FormField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}><FormField label="Title / Role"><TextField value={form.title || ''} onChange={e => set('title', e.target.value)} fullWidth size="small" placeholder="CEO" sx={INPUT_SX} /></FormField></Box>
            <Box sx={{ flex: 1 }}><FormField label="Company"><TextField value={form.company || ''} onChange={e => set('company', e.target.value)} fullWidth size="small" placeholder="GoVirtual Inc." sx={INPUT_SX} /></FormField></Box>
          </Box>
          <FormField label="Bio"><TextField value={form.bio || ''} onChange={e => set('bio', e.target.value)} fullWidth size="small" multiline rows={3} placeholder="Short biography..." sx={INPUT_SX} /></FormField>
          {[{ key: 'linkedin', label: 'LinkedIn', ph: 'https://linkedin.com/in/...' }, { key: 'twitter', label: 'Twitter / X', ph: 'https://twitter.com/...' }, { key: 'website', label: 'Website', ph: 'https://...' }].map(({ key, label, ph }) => (
            <FormField key={key} label={label}><TextField value={form.socialLinks[key] || ''} onChange={e => setSocial(key, e.target.value)} fullWidth size="small" placeholder={ph} error={!!urlErrors[key]} helperText={urlErrors[key] ? 'Enter a valid URL' : ''} sx={INPUT_SX} /></FormField>
          ))}
        </Box>
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e8edf2', flexShrink: 0, background: '#fafbfc' }}>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
            <Button onClick={() => canSave && onSave(form)} variant="contained" startIcon={<SaveOutlined />} disabled={!canSave} sx={{ borderRadius: 2, background: PRIMARY, '&:hover': { background: '#0d5fa8' } }}>Save Speaker</Button>
          </Box>
        </Box>
      </Drawer>
      <ContentBrowserModal open={photoBrowser} onClose={() => setPhotoBrowser(false)} onSelect={url => { set('avatar', url); setPhotoBrowser(false); }} mediaType="image" currentUrl={form.avatar || ''} />
    </>
  );
}

function SpeakerSelector({ selected, eventSpeakers, onChange, hideEdit = false }) {
  const [pickerOpen, setPickerOpen]       = useState(false);
  const [anchorPos, setAnchorPos]         = useState(null);
  const [editorOpen, setEditorOpen]       = useState(false);
  const [editorSpeaker, setEditorSpeaker] = useState(null);
  const [editorIdx, setEditorIdx]         = useState(null);
  const btnRef = useRef();

  useEffect(() => {
    if (!pickerOpen) return;
    const handler = e => {
      const popup = document.getElementById('speaker-picker-popup');
      if (popup && popup.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  const openPicker = () => {
    if (pickerOpen) { setPickerOpen(false); return; }
    if (btnRef.current) {
      const rect   = btnRef.current.getBoundingClientRect();
      const drawer = btnRef.current.closest('.MuiDrawer-paper') || btnRef.current.closest('[class*="MuiPaper"]');
      const dRect  = drawer ? drawer.getBoundingClientRect() : rect;
      setAnchorPos({ bottom: window.innerHeight - rect.top + 6, left: dRect.left, width: dRect.width });
    }
    setPickerOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>Speakers</Typography>
        <Button ref={btnRef} size="small" variant="outlined" startIcon={<PersonAddOutlined sx={{ fontSize: 14 }} />}
          onClick={openPicker} sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem', height: 28, px: 1.25 }}>
          Add Speaker
        </Button>
      </Box>
      {selected.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
          {selected.map((s, i) => (
            <SpeakerCard key={s.id || i} speaker={s} hideEdit={hideEdit}
              onRemove={() => onChange(selected.filter((_, idx) => idx !== i))}
              onEdit={() => { setEditorSpeaker(s); setEditorIdx(i); setEditorOpen(true); }} />
          ))}
        </Box>
      )}
      {pickerOpen && anchorPos && (
        <Box id="speaker-picker-popup" sx={{ position: 'fixed', bottom: anchorPos.bottom, left: anchorPos.left + 16,
          width: anchorPos.width - 32, height: 340, zIndex: 1500, background: '#fff', border: '1px solid #e4e9f0',
          borderRadius: 2, boxShadow: '0 -4px 32px rgba(0,0,0,0.15)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <SpeakerPickerPopup eventSpeakers={eventSpeakers} selected={selected}
            onSelect={sp => { onChange([...selected, sp]); setPickerOpen(false); }}
            onAddCustom={sp => { onChange([...selected, sp]); setPickerOpen(false); }}
            onClose={() => setPickerOpen(false)} />
        </Box>
      )}
      <SpeakerEditorDrawer
        open={editorOpen} speaker={editorSpeaker}
        onClose={() => { setEditorOpen(false); setEditorSpeaker(null); setEditorIdx(null); }}
        onSave={form => {
          onChange(selected.map((s, i) => i === editorIdx ? { ...s, ...form } : s));
          setEditorOpen(false); setEditorSpeaker(null); setEditorIdx(null);
        }}
      />
    </Box>
  );
}

// ─── SessionDrawer (main export) ─────────────────────────────────────────────
export default function SessionDrawer({ open, session, event, onClose, onSave, onDelete, hideEdit = false }) {
  const [form, setForm] = useState({ ...BLANK });

  useEffect(() => {
    if (!open) return;
    setForm(session
      ? { ...BLANK, ...session, startTime: session.timeSlots?.[0]?.startTime || session.startTime || '', endTime: session.timeSlots?.[0]?.endTime || session.endTime || '' }
      : { ...BLANK }
    );
  }, [session, open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 460 }, display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{session ? 'Edit Session' : 'Add Session'}</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {onDelete && (
            <IconButton size="small" onClick={onDelete} sx={{ color: '#d32f2f', '&:hover': { background: alpha('#d32f2f', 0.08) } }}>
              <DeleteOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <IconButton size="small" onClick={onClose}><CloseOutlined sx={{ fontSize: 18 }} /></IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
        <SessionPhotoField value={form.posterUrl || ''} onChange={v => set('posterUrl', v)} />
        <FormField label="Session Title" required>
          <TextField value={form.title} onChange={e => set('title', e.target.value)} fullWidth size="small" placeholder="Opening Keynote" sx={INPUT_SX} />
        </FormField>
        <FormField label="Description">
          <TextField value={form.description || ''} onChange={e => set('description', e.target.value)} fullWidth size="small" multiline rows={2} sx={INPUT_SX} />
        </FormField>
        <FormField label="Location / Room">
          <TextField value={form.location || ''} onChange={e => set('location', e.target.value)} fullWidth size="small" sx={INPUT_SX} />
        </FormField>
        <Divider sx={{ my: 2, borderColor: '#f0f4f8' }} />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TimePicker label="Start Time" value={form.startTime || ''} onChange={v => set('startTime', v)} />
          <TimePicker label="End Time"   value={form.endTime   || ''} onChange={v => set('endTime', v)} />
        </Box>
        <Divider sx={{ my: 2, borderColor: '#f0f4f8' }} />
        <SpeakerSelector
          selected={form.speakers || []}
          eventSpeakers={event?.speakers || []}
          onChange={v => set('speakers', v)}
          hideEdit={hideEdit}
        />
      </Box>

      <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e8edf2', flexShrink: 0, background: '#fafbfc' }}>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={() => onSave(form)} variant="contained" startIcon={<SaveOutlined />}
            disabled={!form.title.trim()}
            sx={{ borderRadius: 2, background: PRIMARY, '&:hover': { background: '#0d5fa8' } }}>
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
